/**
 * PWA / client version helpers (same approach as PixieChatBot).
 * Version format: MMDDYY-N (e.g. 063026-3).
 */

import { persistPushEnabled } from './chatPushState';
import {
  manifestHrefForIconRefresh,
  pointManifestLink,
  purgePwaCaches,
  pwaRefreshUrls,
} from './pwaForceUpdate';
import {
  compareVersions,
  healthVersion,
  isNewerVersion,
  parseVersion,
  versionUpdateOffer,
} from './pwaVersionCheck';

export {
  compareVersions,
  healthVersion,
  isNewerVersion,
  parseVersion,
  versionUpdateOffer,
};

const PWA_UPDATE_TARGET_KEY = 'pixie-pwa-target-version';
const PWA_UPDATE_ATTEMPT_KEY = 'pixie-pwa-update-attempt';
export const PWA_UPDATING_KEY = 'pixie-pwa-updating';
export const PWA_UPDATING_AT_KEY = 'pixie-pwa-updating-at';
const PWA_UPDATE_COOLDOWN_MS = 45000;

function storageGet(key) {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function storageSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (_) {
    /* ignore */
  }
  try {
    localStorage.setItem(key, value);
  } catch (_) {
    /* ignore */
  }
}

function storageRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (_) {
    /* ignore */
  }
  try {
    localStorage.removeItem(key);
  } catch (_) {
    /* ignore */
  }
}

export function markPwaUpdateStarted() {
  storageSet(PWA_UPDATING_KEY, '1');
  storageSet(PWA_UPDATING_AT_KEY, String(Date.now()));
}

/** True while Install is navigating, and for a short window after so a new
 *  service worker claiming the page does not trigger a second reload.
 *  Uses localStorage as well as sessionStorage because some standalone
 *  WebViews drop sessionStorage across location.href. */
export function isPwaUpdateCooldown() {
  try {
    if (storageGet(PWA_UPDATING_KEY) === '1') return true;
    const at = parseInt(storageGet(PWA_UPDATING_AT_KEY) || '0', 10);
    return !!(at && Date.now() - at < PWA_UPDATE_COOLDOWN_MS);
  } catch (_) {
    return false;
  }
}

export function clearPwaUpdateFlagIfExpired() {
  try {
    const at = parseInt(storageGet(PWA_UPDATING_AT_KEY) || '0', 10);
    if (!at || Date.now() - at >= PWA_UPDATE_COOLDOWN_MS) {
      storageRemove(PWA_UPDATING_KEY);
      storageRemove(PWA_UPDATING_AT_KEY);
    }
  } catch (_) {
    /* ignore */
  }
}

export function displayableVersion(...candidates) {
  for (const candidate of candidates) {
    if (candidate && parseVersion(candidate)) return candidate;
  }
  return '';
}

export async function fetchRemoteVersion(axios) {
  const response = await axios.get('/api/health', {
    headers: { 'Cache-Control': 'no-cache' },
    params: { _: Date.now() },
    timeout: 15000,
  });
  return healthVersion(response?.data);
}

export async function hasWaitingServiceWorker() {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  return Boolean(reg?.waiting);
}

/**
 * After Install reload: if we still have the old baked-in version, Install
 * did not load the new client assets (stale HTTP cache or incomplete deploy).
 */
export function getFailedUpdateMessage(localVersion) {
  try {
    const target = sessionStorage.getItem(PWA_UPDATE_TARGET_KEY);
    if (!target) return null;
    if (!parseVersion(target)) {
      sessionStorage.removeItem(PWA_UPDATE_TARGET_KEY);
      sessionStorage.removeItem(PWA_UPDATE_ATTEMPT_KEY);
      return null;
    }
    const cmp = compareVersions(localVersion, target);
    if (cmp != null && cmp >= 0) {
      sessionStorage.removeItem(PWA_UPDATE_TARGET_KEY);
      sessionStorage.removeItem(PWA_UPDATE_ATTEMPT_KEY);
      return null;
    }
    // Local stamp is still older than the install target
    const attempts = parseInt(
      sessionStorage.getItem(PWA_UPDATE_ATTEMPT_KEY) || '0',
      10,
    );
    return {
      target,
      local: localVersion,
      attempts,
    };
  } catch (_) {
    return null;
  }
}

export function clearUpdateAttemptTracking() {
  try {
    sessionStorage.removeItem(PWA_UPDATE_TARGET_KEY);
    sessionStorage.removeItem(PWA_UPDATE_ATTEMPT_KEY);
  } catch (_) {
    /* ignore */
  }
}

async function clearAllCaches() {
  if (!('caches' in window)) return;
  const names = await caches.keys();
  await Promise.all(names.map((name) => caches.delete(name)));
}

async function unregisterAllServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;
  const registrations = await navigator.serviceWorker.getRegistrations();
  for (const registration of registrations) {
    try {
      await registration.unregister();
    } catch (_) {
      /* ignore */
    }
  }
}

/**
 * Best-effort: re-fetch HTML entry and service-worker.js with network-only
 * so the next navigation is less likely to use a disk-cached shell.
 */
async function bustHttpCachesForAppShell(fetchImpl) {
  const fetchFn = fetchImpl || fetch;
  if (typeof fetchFn !== 'function') return;
  const bust = `?_cb=${Date.now()}`;
  const urls = [
    `/index.html${bust}`,
    `/service-worker.js${bust}`,
    `/${bust}`,
    `/mhome${bust}`,
  ];
  await Promise.all(
    urls.map((url) =>
      fetchFn(url, {
        cache: 'reload',
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      }).catch(() => null),
    ),
  );
}

/**
 * Activate waiting SW (if any), clear caches, hard reload onto a cache-busted URL.
 * Pass targetVersion so a failed install (still on old baked-in version) can be detected.
 */
export async function applyPwaUpdate(options = {}) {
  const targetVersion =
    typeof options.targetVersion === 'string' ? options.targetVersion : null;
  const fetchImpl = options.fetchImpl || fetch;

  try {
    markPwaUpdateStarted();
    if (targetVersion && parseVersion(targetVersion)) {
      sessionStorage.setItem(PWA_UPDATE_TARGET_KEY, targetVersion);
      const prev = parseInt(
        sessionStorage.getItem(PWA_UPDATE_ATTEMPT_KEY) || '0',
        10,
      );
      sessionStorage.setItem(PWA_UPDATE_ATTEMPT_KEY, String(prev + 1));
    }
  } catch (_) {
    /* ignore */
  }

  // Unregistering the SW drops the browser PushSubscription. Remember that
  // this device had push enabled so Settings stays "Enabled (push active)"
  // while the new worker resubscribes after reload.
  try {
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      persistPushEnabled(true);
    }
  } catch (_) {
    /* ignore */
  }

  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();

      // Prefer activating a waiting worker first so its precache can control
      // the next load — then still clear outdated caches after claim.
      if (reg?.waiting) {
        await new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve();
          };
          navigator.serviceWorker.addEventListener('controllerchange', finish, {
            once: true,
          });
          try {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          } catch (_) {
            finish();
            return;
          }
          setTimeout(finish, 4000);
        });
      }

      // Also try installing worker if present
      if (reg?.installing) {
        await new Promise((resolve) => {
          const worker = reg.installing;
          if (!worker || worker.state === 'activated') {
            resolve();
            return;
          }
          const t = setTimeout(resolve, 4000);
          worker.addEventListener('statechange', () => {
            if (
              worker.state === 'installed' ||
              worker.state === 'activated' ||
              worker.state === 'redundant'
            ) {
              clearTimeout(t);
              resolve();
            }
          });
        });
        if (reg.waiting) {
          try {
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          } catch (_) {
            /* ignore */
          }
          await new Promise((r) => setTimeout(r, 500));
        }
      }

      // Hard reset: drop all SW + Cache Storage so the next load is forced
      // to the network (avoids a stuck waiting SW never applying).
      await unregisterAllServiceWorkers();
    }

    await clearAllCaches();
    await bustHttpCachesForAppShell(fetchImpl);
    if (options.purgeIcons) {
      let manifest = options.manifest || null;
      if (!manifest) {
        try {
          const response = await fetchImpl('/manifest.json', {
            cache: 'reload',
            credentials: 'same-origin',
          });
          if (response && response.ok && typeof response.json === 'function') {
            manifest = await response.json();
          }
        } catch (_) {
          manifest = null;
        }
      }
      const cacheStorage =
        options.cacheStorage ||
        (typeof caches !== 'undefined' ? caches : null);
      await purgePwaCaches({
        cacheStorage,
        fetchImpl,
        urls: pwaRefreshUrls(manifest),
      });
      pointManifestLink(
        manifestHrefForIconRefresh(targetVersion),
        options.documentRef ||
          (typeof document !== 'undefined' ? document : null),
      );
    }
  } catch (e) {
    console.error('[PWA] applyPwaUpdate cleanup error:', e);
  }

  // Navigate with a strong cache-buster. Prefer start path /mhome (PWA).
  try {
    const u = new URL(window.location.href);
    // Drop prior bust params
    u.searchParams.delete('_swu');
    u.searchParams.delete('_v');
    u.searchParams.set('_swu', String(Date.now()));
    if (targetVersion) {
      u.searchParams.set('_v', targetVersion);
    }
    // Standalone PWAs sometimes ignore replace() — href is more reliable
    window.location.href = u.pathname + u.search + u.hash;
  } catch (_) {
    window.location.reload();
  }
}
